import {useState} from 'react';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import {ROUTE_PATHS} from '@di-shared/routes';
import {sanitizeLicensePlateNumber} from '@di-shared/utils/vehicle';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import usePendingHandler from '@shared/usePendingHandler';

export interface HighWayUnpaidFormType {
  licensePlateNumber: string;
}

export interface HighWayUnpaidFormErrorType {
  licensePlateNumberRegex: boolean;
}

export interface HighwayUnpaidEditSearchFormProps {
  vin: string;
  licensePlateNumber: string;
}

function HighwayUnpaidEditSearchForm({vin, licensePlateNumber}: HighwayUnpaidEditSearchFormProps) {
  const {t} = useInferableTranslation();
  const puid = useEnvironmentStore((state) => state.puid);
  const {openAlert} = useCcWebviewUtilsHelper();
  const navigate = useNavigate();

  const [form, setForm] = useState<HighWayUnpaidFormType>({licensePlateNumber: ''});
  const [errors, setErrors] = useState<HighWayUnpaidFormErrorType>({
    licensePlateNumberRegex: false,
  });

  function updateForm(partial: Partial<HighWayUnpaidFormType>) {
    const licensePlateNumber = sanitizeLicensePlateNumber(form.licensePlateNumber, partial.licensePlateNumber);

    const newForm: HighWayUnpaidFormType = {...form, ...partial, licensePlateNumber};
    setForm(newForm);
  }

  const [searchHighwayUnpaidFee] = usePendingHandler(
    async () => {
      setErrors((prev) => ({...prev, licensePlateNumberRegex: false}));
      const isConfirm = await ccWebviewUtils.openBottomSheetWebViewUrl(
        ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_HIGHWAY_UNPAID_EDIT_CONFIRM.getFullPath(),
        {
          beforeLicensePlate: licensePlateNumber,
          afterLicensePlate: form.licensePlateNumber.trim(),
        }
      );

      if (isConfirm) {
        await diApi.highwayUnpaid.updateVehicle({
          puid,
          licensePlateNumber,
          requestBody: {
            newLicensePlateNumber: form.licensePlateNumber,
          },
        });

        // [2025-12-12:cy.seo] FIXME: PPP1  이것도
        // 미동의 -> 고속도로 어바웃 -> 약관 동의 -> 차량 등록 -> 통행료 조회하기 -> 다시 차량유지탭으로
        // 이렇게 하면 뒤로가기 했을떄 이 화면으로 돌아옴;;;
        navigate(ROUTE_PATHS.HOME_FINANCE_TAB.getFullPath(vin));
      }
    },

    async (error: any) => {
      if (error?.code === 4000) {
        setErrors((prev) => ({...prev, licensePlateNumberRegex: true}));
        return;
      }
      await openAlert({
        title: t('T40169'),
        message: `error [${error?.resultCode || error?.code || 9999}]`,
      });
    }
  );
  return (
    <>
      <main
        id="content"
        className="content"
      >
        <section className="section section_toll_inquiry">
          <div className="section_heading">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T20392')}
            </h2>
            <p
              className="section_heading_desc"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T20393')}
            </p>
          </div>
          <div className="service_info_area">
            <h3 className="info_title">{t('T20394')}</h3>
            <div className="input_info">
              <label
                htmlFor="vehicleRegistrationNumber01"
                className="blind"
              >
                {t('T20395')}
              </label>
              <div className="input_box input_box_field">
                <input
                  type="text"
                  id="vehicleRegistrationNumber01"
                  className="input_text body_large_r"
                  inputMode="text"
                  placeholder={t('T20395')}
                  value={licensePlateNumber}
                  readOnly={true}
                />
                <div className="box_right">
                  <button
                    type="button"
                    className="btn_delete_input"
                  >
                    <span className="ic ic_filled_delete_20px">{t('T10154')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="service_info_area">
            <h3 className="info_title">{t('T20396')}</h3>
            <div className="input_info">
              <label
                htmlFor="vehicleRegistrationNumber02"
                className="blind"
              >
                {t('T20396')}
              </label>
              <div className="input_box input_box_field">
                <input
                  type="text"
                  id="vehicleRegistrationNumber02"
                  className="input_text body_large_r"
                  inputMode="text"
                  placeholder={t('T20397')}
                  value={form.licensePlateNumber}
                  onChange={(e) => updateForm({licensePlateNumber: e.target.value})}
                />
                <div className="box_right">
                  <button
                    type="button"
                    className="btn_delete_input"
                  >
                    <span className="ic ic_filled_delete_20px">{t('T10154')}</span>
                  </button>
                </div>
              </div>
            </div>
            {errors.licensePlateNumberRegex && <p className="txt_error body_small_r">{t('T20398')}</p>}
          </div>

          <ul className="notice_list">
            <li className="notice_item">{t('T20399')}</li>
            <li className="notice_item">{t('T20400')}</li>
          </ul>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => searchHighwayUnpaidFee()}
        >
          {t('T20401')}
        </button>
      </div>
    </>
  );
}

export default HighwayUnpaidEditSearchForm;
